import React from "react";
import { Link } from "react-router-dom";
import {
    Zap,
    Radio,
    ShieldCheck,
    Thermometer,
    Droplets,
    ScanSearch,
    Maximize2,
    Layers3,
    ArrowRight,
    Activity,
    CheckCircle2,
} from "lucide-react";

const STAGES = [
    {
        id: "01",
        title: "Wireless power transfer",
        desc: "An ELWIS transmission pad, installed in the floor or parking surface, generates a controlled magnetic field across the transfer gap using a precisely tuned resonant coil.",
    },
    {
        id: "02",
        title: "Intelligent alignment",
        desc: "The system detects the receiver above the pad and manages positioning tolerance so that everyday parking is precise enough for a reliable charging link.",
    },
    {
        id: "03",
        title: "Industrial reliability",
        desc: "Every component is engineered for continuous commercial operation — repeatable, monitored and built to survive the environment it runs in.",
    },
    {
        id: "04",
        title: "Safe operation",
        desc: "Foreign-object detection, thermal management and continuous supervision keep every transfer safe, controlled and traceable.",
    },
    {
        id: "05",
        title: "Modular architecture",
        desc: "The platform is built around modular building blocks — designed to integrate cleanly into vehicles, sites and infrastructure, and to scale with future capability.",
    },
];

const BENEFITS = [
    { icon: CheckCircle2, t: "Reliable", d: "Designed and validated for dependable daily commercial operation." },
    { icon: Activity, t: "Automated", d: "No plug-in step. Charging starts and stops without human intervention." },
    { icon: Radio, t: "Contactless", d: "Energy is transferred wirelessly — no exposed connectors, no cables to manage." },
    { icon: ShieldCheck, t: "Safe by design", d: "Foreign-object detection and thermal monitoring on every transfer." },
    { icon: Layers3, t: "Modular", d: "Standardized building blocks make integration and future upgrades straightforward." },
    { icon: Maximize2, t: "Scalable", d: "Architecture built to grow with the site, the fleet and the roadmap." },
];

const SAFETY = [
    { icon: ShieldCheck, t: "Foreign Object Detection", d: "Continuous sensing prevents transfer when an object enters the field zone." },
    { icon: Thermometer, t: "Thermal protection", d: "Active monitoring of pad and receiver thermal envelopes." },
    { icon: Droplets, t: "Waterproof integration", d: "Fully sealed surfaces — IP67 / IP68 grade across product family." },
    { icon: ScanSearch, t: "Live link diagnostics", d: "Every transfer is logged, audited and remotely supervised." },
];

const SCALES = [
    { label: "Current platform", power: "Up to 11 kW", use: "Wireless AC and DC charging for commercial and industrial applications." },
    { label: "Charging type", power: "AC and DC", use: "Proven, standards-aligned wireless energy transfer across AC and DC use cases." },
    { label: "Architecture", power: "Modular", use: "Designed for future scalability across new use cases and power classes." },
    { label: "Deployment", power: "Commercial-ready", use: "Real-world deployments across fleets, sites and infrastructure." },
];

const Technology = () => {
    return (
        <main data-testid="technology-page" className="bg-white text-[#050B14]">
            {/* HERO */}
            <section className="relative bg-[#050B14] text-white pt-32 pb-24 lg:pb-32 overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-50" />
                <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#00E5FF]/10 blur-3xl" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="max-w-3xl">
                        <div className="kicker text-[#00E5FF] mb-6 flex items-center gap-3">
                            <span className="w-8 h-px bg-[#00E5FF]" />
                            Technology
                        </div>
                        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                            Built for <span className="text-gradient-cyan">dependable</span> daily operation.
                        </h1>
                        <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-2xl">
                            ELWIS wireless charging is engineered around a modular,
                            industrially reliable architecture — designed to charge
                            commercial vehicles and equipment automatically, safely and
                            day after day.
                        </p>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section data-testid="tech-how-it-works" className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-5">
                            <div className="kicker text-[#0055FF] mb-5">01 / How it works</div>
                            <h2 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0F1C]">
                                Five stages, no cable.
                            </h2>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="text-slate-600 leading-relaxed">
                                Every ELWIS deployment is a five-stage chain: a grid
                                input, a tuned transmitter, a controlled magnetic link, a
                                matched receiver and an intelligent management layer. The
                                cable is the only thing missing.
                            </p>
                        </div>
                    </div>

                    <div className="border border-slate-200">
                        {STAGES.map((s, i) => (
                            <div
                                key={s.id}
                                className={`grid grid-cols-12 gap-6 p-6 lg:p-10 ${
                                    i !== STAGES.length - 1 ? "border-b border-slate-200" : ""
                                } hover:bg-slate-50 transition-colors`}
                            >
                                <div className="col-span-12 lg:col-span-2 font-mono text-sm text-[#0055FF]">{s.id}</div>
                                <div className="col-span-12 lg:col-span-4">
                                    <h3 className="font-display font-bold text-xl lg:text-2xl text-[#0A0F1C]">{s.title}</h3>
                                </div>
                                <div className="col-span-12 lg:col-span-6">
                                    <p className="text-slate-600 leading-relaxed">{s.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TECHNICAL BENEFITS */}
            <section data-testid="tech-benefits" className="py-24 lg:py-32 bg-[#FAFAFA]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="max-w-2xl mb-12">
                        <div className="kicker text-[#0055FF] mb-5">02 / Technical benefits</div>
                        <h2 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0F1C]">
                            Built for the real conditions of industry.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                        {BENEFITS.map((b, idx) => (
                            <div key={b.t} className="bg-white p-8">
                                <div className="flex items-center justify-between mb-5">
                                    <span className="font-mono text-xs text-slate-400">B/0{idx + 1}</span>
                                    <b.icon className="w-5 h-5 text-[#0055FF]" />
                                </div>
                                <h3 className="font-display font-bold text-lg text-[#0A0F1C]">{b.t}</h3>
                                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{b.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SAFETY & RELIABILITY — DARK */}
            <section data-testid="tech-safety" className="py-24 lg:py-32 bg-[#050B14] text-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-12 mb-14">
                        <div className="lg:col-span-7">
                            <div className="kicker text-[#00E5FF] mb-5">03 / Safety & reliability</div>
                            <h2 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                                Industrial-grade. <span className="text-gradient-cyan">Audit-ready.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-5">
                            <p className="text-white/60 leading-relaxed">
                                Wireless does not mean uncontrolled. Every transfer is
                                sensed, supervised and certified.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
                        {SAFETY.map((s) => (
                            <div key={s.t} className="bg-[#050B14] p-8 hover:bg-[#0A1220] transition-colors">
                                <s.icon className="w-6 h-6 text-[#00E5FF]" />
                                <h3 className="mt-5 font-display font-bold text-xl">{s.t}</h3>
                                <p className="mt-3 text-sm text-white/70 leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SCALABILITY */}
            <section data-testid="tech-scalability" className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="max-w-2xl mb-12">
                        <div className="kicker text-[#0055FF] mb-5">04 / Current platform</div>
                        <h2 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0F1C]">
                            Proven today. Built to grow.
                        </h2>
                        <p className="mt-6 text-slate-600 leading-relaxed">
                            ELWIS is transparent about what its platform delivers now
                            and how it is designed to evolve. Today's system focuses on
                            reliable wireless AC and DC charging for real commercial and
                            industrial deployments.
                        </p>
                    </div>

                    <div className="border border-slate-200">
                        <div className="hidden lg:grid grid-cols-12 gap-6 px-8 py-5 bg-[#0A0F1C] text-white kicker">
                            <div className="col-span-3">Capability</div>
                            <div className="col-span-3">Specification</div>
                            <div className="col-span-6">Notes</div>
                        </div>
                        {SCALES.map((s, i) => (
                            <div
                                key={s.label}
                                className={`grid grid-cols-12 gap-6 px-6 lg:px-8 py-6 ${
                                    i !== SCALES.length - 1 ? "border-b border-slate-200" : ""
                                } hover:bg-slate-50`}
                            >
                                <div className="col-span-12 lg:col-span-3 font-display font-bold text-lg text-[#0A0F1C]">{s.label}</div>
                                <div className="col-span-12 lg:col-span-3 font-mono text-[#0055FF]">{s.power}</div>
                                <div className="col-span-12 lg:col-span-6 text-slate-600">{s.use}</div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 flex flex-col sm:flex-row gap-3">
                        <Link
                            to="/industries"
                            data-testid="tech-cta-industries"
                            className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-6 py-3.5 rounded-md text-sm font-semibold hover:bg-[#0044CC] transition-colors"
                        >
                            See deployment scenarios
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="tech-cta-contact"
                            className="inline-flex items-center gap-2 border border-slate-300 text-[#0A0F1C] px-6 py-3.5 rounded-md text-sm font-semibold hover:border-[#0055FF] hover:text-[#0055FF] transition-colors"
                        >
                            Request a technical discussion
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Technology;
